import React, { memo, useCallback, useEffect, useState } from 'react'
import { Skeleton, Tabs, Tooltip } from 'antd'
import { ArrowRightOutlined, CheckOutlined, MinusOutlined, ReloadOutlined } from '@ant-design/icons'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import { getWindrawStageMarkState, WINDRAW_STAGE_NAMES } from './stage-marks'
import { getWindrawStatusTone } from './status'
import { ReadinessReferencePanel } from './readiness-reference'
import {
  DetailsModal,
  DetailsOverview,
  DetailsOverviewItem,
  DetailsOverviewLabel,
  DetailsOverviewValue,
  DetailsTable,
  Empty,
  Fact,
  FactLabel,
  FactValue,
  Facts,
  FieldGridLayout,
  Freshness,
  Loading,
  Message,
  MoreButton,
  OrderSummary,
  Panel,
  PanelBody,
  PanelHeader,
  DetailsNote,
  EventMarkNode,
  EventMarkSegment,
  EventMarkTrack,
  PositionMeta,
  PositionName,
  RefreshButton,
  StageMark,
  Status,
  TableScroll,
} from './styles'

interface WindrawPosition {
  id: number
  number: number | null
  name: string
  quantity: number | null
  width: number | null
  height: number | null
  profileSystem: string
  fittingSystem: string
  colorInside: string
  colorOutside: string
  isProduct: boolean
}

interface WindrawStage {
  name: string
  plannedFrom: string | null
  plannedTo: string | null
  lastFactAt: string | null
  totalCount: number
  completedCount: number
}

interface WindrawOrder {
  id: number
  number: string
  documentState: string
  destination: string
  updatedAt: string | null
  shipment: {
    plannedFrom: string | null
    plannedTo: string | null
    completedAt: string | null
    totalCount: number
    completedCount: number
  }
  positionCount: number
  productPositionCount: number
  productQuantity: number
  productWeight: number | null
  productArea: number | null
  positions: WindrawPosition[]
  stages: WindrawStage[]
}

interface WindrawLookup {
  productionNumbers: string[]
  missingNumbers: string[]
  orders: WindrawOrder[]
  fetchedAt: string | null
  stale: boolean
}

interface WindrawOrderPanelProps {
  orderId: string | number
  productionNumber?: string | null
}

const formatDate = (value?: string | null): string => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '—'
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return formatDate(value)
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const formatQuantity = (value: number | null): string =>
  value === null ? '—' : new Intl.NumberFormat('ru-RU').format(value)

const formatMeasurement = (value: number | null | undefined, unit: string): string =>
  value == null
    ? '—'
    : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value)} ${unit}`

const formatSize = (position: WindrawPosition): string =>
  position.width && position.height ? `${position.width} × ${position.height} мм` : '—'

const formatDateRange = (from?: string | null, to?: string | null): string => {
  if (!from && !to) return '—'
  if (!from || !to || formatDate(from) === formatDate(to)) return formatDate(to || from)
  return `${formatDate(from)} — ${formatDate(to)}`
}

const shipmentFactText = (order: WindrawOrder): string => {
  const { shipment } = order
  if (shipment.completedAt) return formatDate(shipment.completedAt)
  if (shipment.totalCount && shipment.completedCount) return 'Отмечено частично'
  return 'Пока нет'
}

const stageMarkText = (stage: WindrawStage): string => {
  if (!stage.completedCount) return 'Нет отметки'
  return stage.completedCount < stage.totalCount ? 'Частично' : 'Отмечено'
}

const OrderStageMarks = ({ stages }: { stages: WindrawStage[] }) => {
  const marks = WINDRAW_STAGE_NAMES.map(name => {
    const stage = stages.find(item => item.name === name)
    return { name, stage, state: getWindrawStageMarkState(stage) }
  })

  return (
    <EventMarkTrack
      role="group"
      aria-label="Отметки операций WinDraw, не процент готовности заказа"
    >
      {marks.map(({ name, stage, state }, index) => {
        const description = stage
          ? `${name}: ${stageMarkText(stage)}, ${stage.completedCount} из ${
              stage.totalCount
            } записей`
          : `${name}: нет данных по событию`

        return (
          <Tooltip
            key={name}
            placement="top"
            mouseEnterDelay={0.1}
            title={
              <div>
                <strong>{name}</strong>
                <div>
                  {stage
                    ? `${stageMarkText(stage)} · ${stage.completedCount} из ${
                        stage.totalCount
                      } записей`
                    : 'Нет данных по событию'}
                </div>
                {stage && <div>План: {formatDateRange(stage.plannedFrom, stage.plannedTo)}</div>}
                {stage && <div>Факт: {formatDate(stage.lastFactAt)}</div>}
              </div>
            }
          >
            <EventMarkSegment
              $connected={index > 0 && marks[index - 1].state === 'marked' && state === 'marked'}
              role="img"
              aria-label={description}
              tabIndex={0}
            >
              <EventMarkNode $state={state}>
                {state === 'marked' && <CheckOutlined aria-hidden="true" />}
                {state === 'partial' && <MinusOutlined aria-hidden="true" />}
              </EventMarkNode>
              <span>{name}</span>
            </EventMarkSegment>
          </Tooltip>
        )
      })}
    </EventMarkTrack>
  )
}

const positionProperties = (position: WindrawPosition): string[] =>
  [
    position.profileSystem,
    position.fittingSystem,
    position.colorInside && position.colorOutside
      ? position.colorInside === position.colorOutside
        ? position.colorInside
        : `${position.colorInside} / ${position.colorOutside}`
      : position.colorInside || position.colorOutside,
  ].filter(Boolean)

const PositionsTable = ({
  positions,
  showProperties = false,
}: {
  positions: WindrawPosition[]
  showProperties?: boolean
}) => (
  <TableScroll>
    <DetailsTable>
      <thead>
        <tr>
          <th>№</th>
          <th>Позиция</th>
          <th>Количество</th>
          <th>Размер</th>
        </tr>
      </thead>
      <tbody>
        {positions.map(position => {
          const properties = showProperties ? positionProperties(position) : []
          return (
            <tr key={position.id}>
              <td>{position.number ?? '—'}</td>
              <td>
                <PositionName>{position.name || 'Без названия'}</PositionName>
                {properties.length > 0 && <PositionMeta>{properties.join(' · ')}</PositionMeta>}
              </td>
              <td>{formatQuantity(position.quantity)}</td>
              <td>{formatSize(position)}</td>
            </tr>
          )
        })}
      </tbody>
    </DetailsTable>
  </TableScroll>
)

const WindrawOrderSummary = ({
  order,
  onDetails,
}: {
  order: WindrawOrder
  onDetails: (order: WindrawOrder) => void
}) => {
  const hasStageMarks = WINDRAW_STAGE_NAMES.some(name =>
    order.stages.some(stage => stage.name === name),
  )

  return (
    <OrderSummary>
      <Facts>
        <Fact>
          <FactLabel>Номер заказа</FactLabel>
          <FactValue>№{order.number}</FactValue>
        </Fact>
        {order.documentState && (
          <Fact>
            <FactLabel>Статус в WinDraw</FactLabel>
            <FactValue>
              <Status $tone={getWindrawStatusTone(order.documentState)}>
                {order.documentState}
              </Status>
            </FactValue>
          </Fact>
        )}
        {hasStageMarks && (
          <Fact>
            <FactLabel>Отметки операций WD</FactLabel>
            <FactValue>
              <OrderStageMarks stages={order.stages} />
            </FactValue>
          </Fact>
        )}
        <Fact>
          <FactLabel>Изделия</FactLabel>
          <FactValue>
            {order.productPositionCount
              ? `${formatQuantity(order.productQuantity)} шт. · ${order.productPositionCount} поз.`
              : 'Нет данных'}
          </FactValue>
        </Fact>
        <Fact>
          <FactLabel>Площадь изделий</FactLabel>
          <FactValue>{formatMeasurement(order.productArea, 'м²')}</FactValue>
        </Fact>
        <Fact>
          <FactLabel>Вес изделий</FactLabel>
          <FactValue>{formatMeasurement(order.productWeight, 'кг')}</FactValue>
        </Fact>
        <Fact>
          <FactLabel>План отгрузки</FactLabel>
          <FactValue>
            {formatDateRange(order.shipment.plannedFrom, order.shipment.plannedTo)}
          </FactValue>
        </Fact>
        <Fact>
          <FactLabel>Факт отгрузки</FactLabel>
          <FactValue>{shipmentFactText(order)}</FactValue>
        </Fact>
        <Fact>
          <FactLabel>Пункт назначения</FactLabel>
          <FactValue>{order.destination || '—'}</FactValue>
        </Fact>
      </Facts>
      <MoreButton type="primary" onClick={() => onDetails(order)}>
        Подробнее о заказе <ArrowRightOutlined aria-hidden="true" />
      </MoreButton>
    </OrderSummary>
  )
}

const WindrawOrderDetails = ({ order }: { order: WindrawOrder }) => {
  const timeline = WINDRAW_STAGE_NAMES.map(name =>
    order.stages.find(stage => stage.name === name),
  ).filter((stage): stage is WindrawStage => Boolean(stage))
  const products = order.positions.filter(position => position.isProduct)
  const relatedPositions = order.positions.filter(position => !position.isProduct)
  const relatedPositionCount = Math.max(0, order.positionCount - order.productPositionCount)

  return (
    <div>
      <DetailsOverview>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>Статус</DetailsOverviewLabel>
          <DetailsOverviewValue>
            <Status $tone={getWindrawStatusTone(order.documentState)}>
              {order.documentState || 'Не указан'}
            </Status>
          </DetailsOverviewValue>
        </DetailsOverviewItem>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>Площадь изделий</DetailsOverviewLabel>
          <DetailsOverviewValue>{formatMeasurement(order.productArea, 'м²')}</DetailsOverviewValue>
        </DetailsOverviewItem>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>Вес изделий</DetailsOverviewLabel>
          <DetailsOverviewValue>
            {formatMeasurement(order.productWeight, 'кг')}
          </DetailsOverviewValue>
        </DetailsOverviewItem>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>План отгрузки</DetailsOverviewLabel>
          <DetailsOverviewValue>
            {formatDateRange(order.shipment.plannedFrom, order.shipment.plannedTo)}
          </DetailsOverviewValue>
        </DetailsOverviewItem>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>Факт отгрузки</DetailsOverviewLabel>
          <DetailsOverviewValue>{shipmentFactText(order)}</DetailsOverviewValue>
        </DetailsOverviewItem>
        <DetailsOverviewItem>
          <DetailsOverviewLabel>Пункт назначения</DetailsOverviewLabel>
          <DetailsOverviewValue>{order.destination || '—'}</DetailsOverviewValue>
        </DetailsOverviewItem>
      </DetailsOverview>
      <Tabs
        items={[
          {
            key: 'timeline',
            label: 'Ход заказа',
            children: (
              <>
                {timeline.length ? (
                  <>
                    <DetailsNote>
                      Плановые и фактические отметки WinDraw. «Частично» означает, что факт указан
                      не для всех записей операции.
                    </DetailsNote>
                    <TableScroll>
                      <DetailsTable>
                        <thead>
                          <tr>
                            <th>Событие</th>
                            <th>План</th>
                            <th>Факт</th>
                            <th>Отметка в WD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timeline.map(stage => (
                            <tr key={stage.name}>
                              <td>{stage.name}</td>
                              <td>{formatDateRange(stage.plannedFrom, stage.plannedTo)}</td>
                              <td>{formatDate(stage.lastFactAt)}</td>
                              <td>
                                <StageMark $muted={!stage.completedCount}>
                                  {stageMarkText(stage)}
                                </StageMark>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </DetailsTable>
                    </TableScroll>
                  </>
                ) : (
                  <Empty>Ключевые даты пока не найдены.</Empty>
                )}
              </>
            ),
          },
          {
            key: 'products',
            label: `Изделия · ${formatQuantity(order.productQuantity)} шт.`,
            children: products.length ? (
              <>
                <DetailsNote>
                  Основные изделия: {order.productPositionCount} позиций,{' '}
                  {formatQuantity(order.productQuantity)} шт. Комплектующие и услуги вынесены
                  отдельно.
                </DetailsNote>
                <PositionsTable positions={products} showProperties />
                {order.productPositionCount > products.length && (
                  <Empty>
                    Показаны первые {products.length} из {order.productPositionCount} позиций
                    изделий.
                  </Empty>
                )}
              </>
            ) : (
              <Empty>Основные изделия пока не найдены.</Empty>
            ),
          },
          {
            key: 'related',
            label: `Остальные позиции · ${relatedPositionCount}`,
            children: relatedPositions.length ? (
              <>
                <DetailsNote>
                  Дополнительные строки WinDraw: комплектующие, упаковка и услуги. Они не входят в
                  количество изделий.
                </DetailsNote>
                <PositionsTable positions={relatedPositions} />
                {relatedPositionCount > relatedPositions.length && (
                  <Empty>
                    Показаны первые {relatedPositions.length} из {relatedPositionCount} остальных
                    позиций.
                  </Empty>
                )}
              </>
            ) : (
              <Empty>Других позиций нет.</Empty>
            ),
          },
        ]}
      />
    </div>
  )
}

export const WindrawOrderPanel: React.FC<WindrawOrderPanelProps> = memo(
  ({ orderId, productionNumber }) => {
    const [lookup, setLookup] = useState<WindrawLookup | null>(null)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState('')
    const [selectedOrder, setSelectedOrder] = useState<WindrawOrder | null>(null)

    const load = useCallback(
      async (refresh: boolean, signal?: AbortSignal) => {
        if (refresh) setRefreshing(true)
        else setLoading(true)
        setError('')
        try {
          const suffix = refresh ? '?refresh=1' : ''
          const response = await fetchAPI(
            `/api/order/${orderId}/windraw${suffix}`,
            { signal, timeoutMs: 45_000 },
            true,
          )
          if (signal?.aborted) return
          if (response.error) {
            setError(response.error.message || 'Не удалось получить данные из WinDraw')
          } else {
            setLookup(response as unknown as WindrawLookup)
          }
        } catch {
          if (!signal?.aborted) setError('Не удалось получить данные из WinDraw')
        } finally {
          if (!signal?.aborted) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [orderId],
    )

    useEffect(() => {
      if (orderId === 'new') {
        setLoading(false)
        return
      }
      if (!productionNumber?.trim()) {
        setLoading(false)
        setLookup(null)
        return
      }
      const controller = new AbortController()
      setLookup(null)
      setSelectedOrder(null)
      setLoading(true)
      load(false, controller.signal)
      return () => controller.abort()
    }, [load, orderId, productionNumber])

    if (orderId === 'new') return null

    if (!productionNumber?.trim() || (lookup && lookup.orders.length === 0)) {
      return (
        <ReadinessReferencePanel
          orderId={orderId}
          orderNotFound={Boolean(productionNumber?.trim())}
          onCheckOrder={() => load(true)}
          checkingOrder={refreshing}
          orderLookupError={error}
        />
      )
    }

    return (
      <>
        <FieldGridLayout />
        <Panel aria-label="Данные WinDraw">
          <PanelHeader>
            <h3>WinDraw</h3>
            <RefreshButton
              type="text"
              size="small"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={() => load(true)}
              disabled={loading || refreshing}
              aria-label="Обновить данные WinDraw"
              title="Обновить данные WinDraw"
            />
          </PanelHeader>

          <PanelBody>
            {loading && !lookup ? (
              <Loading aria-live="polite">
                <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 3 }} />
              </Loading>
            ) : (
              <>
                {error && (
                  <Message $error role="alert">
                    {lookup
                      ? 'Не удалось обновить данные. Показана последняя загруженная версия.'
                      : error}
                  </Message>
                )}
                {lookup?.stale && (
                  <Message role="status">
                    WinDraw сейчас недоступен. Данные могут быть устаревшими.
                  </Message>
                )}
                {lookup && lookup.productionNumbers.length === 0 && (
                  <Message>
                    Укажите номер производственного заказа, чтобы увидеть данные WinDraw.
                  </Message>
                )}
                {lookup && lookup.orders.length === 0 && lookup.productionNumbers.length > 0 && (
                  <Message>Заказ с таким номером в WinDraw пока не найден.</Message>
                )}
                {lookup?.orders.map(order => (
                  <WindrawOrderSummary key={order.id} order={order} onDetails={setSelectedOrder} />
                ))}
                {lookup && lookup.missingNumbers.length > 0 && lookup.orders.length > 0 && (
                  <Message>Не найдены в WinDraw: {lookup.missingNumbers.join(', ')}.</Message>
                )}
              </>
            )}
            {lookup?.fetchedAt && (
              <Freshness>
                {lookup.stale ? 'Последние сохранённые данные' : 'Обновлено'} ·{' '}
                {formatDateTime(lookup.fetchedAt)}
              </Freshness>
            )}
          </PanelBody>
        </Panel>
        <DetailsModal
          open={Boolean(selectedOrder)}
          title={selectedOrder ? `Заказ WinDraw №${selectedOrder.number}` : 'Заказ WinDraw'}
          onCancel={() => setSelectedOrder(null)}
          footer={null}
          width={980}
          destroyOnHidden
        >
          {selectedOrder && <WindrawOrderDetails order={selectedOrder} />}
        </DetailsModal>
      </>
    )
  },
)

WindrawOrderPanel.displayName = 'WindrawOrderPanel'
